import React, { useEffect, useRef } from 'react';

export const FluidMeshCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const isMobileOrReduced =
      window.innerWidth <= 900 ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 900px), (hover: none) and (pointer: coarse), (prefers-reduced-motion: reduce)').matches);

    // On mobile/touch devices, rely on the lightweight CSS static gradient in .fluid-canvas-container::after
    // instead of allocating a full-viewport high-DPR 2D canvas or running a 60fps main-thread rAF loop.
    if (isMobileOrReduced) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let t = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    window.addEventListener('resize', resize);
    resize();

    // 4 organic Google colors using semantic HSL (Blue, Emerald, Amber/Gold, Coral)
    const colors = [
      'hsl(217 91% 60% / 0.16)', // Google Blue
      'hsl(156 76% 46% / 0.14)', // Google Emerald
      'hsl(348 83% 62% / 0.13)', // Google Coral
      'hsl(43 96% 56% / 0.14)'   // Google Amber/Gold
    ];

    const render = () => {
      if (document.visibilityState === 'hidden') {
        animId = requestAnimationFrame(render);
        return;
      }

      t += 0.003;
      const w = window.innerWidth;
      const h = window.innerHeight;

      ctx.clearRect(0, 0, w, h);

      // Draw 4 smoothly drifting soft energy lobes
      const lobes = [
        { cx: w * 0.25 + Math.sin(t * 0.7) * (w * 0.15), cy: h * 0.3 + Math.cos(t * 0.9) * (h * 0.15), r: Math.min(w, h) * 0.45, color: colors[0] },
        { cx: w * 0.75 + Math.cos(t * 0.8) * (w * 0.15), cy: h * 0.25 + Math.sin(t * 0.6) * (h * 0.15), r: Math.min(w, h) * 0.5, color: colors[1] },
        { cx: w * 0.35 + Math.cos(t * 0.6) * (w * 0.2), cy: h * 0.75 + Math.sin(t * 0.8) * (h * 0.15), r: Math.min(w, h) * 0.48, color: colors[2] },
        { cx: w * 0.8 + Math.sin(t * 0.5) * (w * 0.15), cy: h * 0.8 + Math.cos(t * 0.7) * (h * 0.15), r: Math.min(w, h) * 0.42, color: colors[3] }
      ];

      for (const lobe of lobes) {
        const gradient = ctx.createRadialGradient(lobe.cx, lobe.cy, lobe.r * 0.1, lobe.cx, lobe.cy, lobe.r);
        gradient.addColorStop(0, lobe.color);
        gradient.addColorStop(1, 'hsl(220 20% 6% / 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(lobe.cx, lobe.cy, lobe.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="fluid-canvas-container">
      <canvas
        id="fluid-mesh-canvas"
        ref={canvasRef}
        style={{ width: '100vw', height: '100vh', display: 'block' }}
      />
    </div>
  );
};
